import { useState, useEffect, useRef } from 'react';
import { analysisApi } from '../api/analysisApi';
import { normalizeTask } from '../components/processing/taskPresentation';

/**
 * Custom hook to monitor processing queue
 */
export const useProcessingMonitor = (isLoggedIn) => {
  const [processingQueue, setProcessingQueue] = useState([]);
  const [currentProcessing, setCurrentProcessing] = useState(null);
  const [processingPanelOpen, setProcessingPanelOpen] = useState(false);
  const pollingIntervalRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const requestPendingRef = useRef(false);
  const [queueUnavailable, setQueueUnavailable] = useState(false);
  const [lastQueueRefresh, setLastQueueRefresh] = useState(null);

  const sortQueueByActivity = (items = []) => {
    const priority = {
      processing: 3,
      translating: 3,
      analyzing: 3,
      running: 3,
      pending: 2,
      queued: 2,
      failed: 1,
      completed: 0,
    };

    return [...items].sort((a, b) => {
      const statusA = String(a?.status || '').toLowerCase();
      const statusB = String(b?.status || '').toLowerCase();
      const priorityA = priority[statusA] ?? 1;
      const priorityB = priority[statusB] ?? 1;

      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }

      const dateA = a?.last_activity_at || a?.updated_at || a?.started_at || a?.created_at || '';
      const dateB = b?.last_activity_at || b?.updated_at || b?.started_at || b?.created_at || '';
      return String(dateB).localeCompare(String(dateA));
    });
  };

  const fetchProcessingQueue = async () => {
    if (!isLoggedIn || requestPendingRef.current) return;
    requestPendingRef.current = true;
    const requestSequence = ++requestSequenceRef.current;
    
    try {
      const data = await analysisApi.getProcessingQueue();
      if (requestSequence !== requestSequenceRef.current) return;
      
      // Map backend response to frontend format
      const mappedQueue = sortQueueByActivity((data.queue || []).map(normalizeTask));
      
      const mappedCurrentTask = data.current_task ? {
        ...normalizeTask(data.current_task),
        type: data.current_task.type || 'translation',
        can_resume: data.current_task.can_resume ?? data.current_task.canResume ?? null,
        status: data.current_task.status || 'pending',
        translated_requirements: data.current_task.translated_requirements ?? 0,
        analyzed_requirements: data.current_task.analyzed_requirements ?? 0,
        // Import-specific fields
        processed_requirements: data.current_task.processed_requirements ?? 0,
        created_requirements: data.current_task.created_requirements ?? 0,
        updated_requirements: data.current_task.updated_requirements ?? 0
      } : null;
      
      setProcessingQueue(mappedQueue);
      setCurrentProcessing(mappedCurrentTask);
      setQueueUnavailable(false);
      setLastQueueRefresh(new Date().toISOString());
    } catch (error) {
      if (requestSequence === requestSequenceRef.current) setQueueUnavailable(true);
    } finally {
      if (requestSequence === requestSequenceRef.current) requestPendingRef.current = false;
    }
  };

  const handleSetProcessingPanelOpen = (isOpen) => {
    setProcessingPanelOpen(isOpen);
  };

  const clearAllCeleryTasks = async () => {
    try {
      await analysisApi.clearAllCeleryTasks();
      // Refresh the queue immediately after clearing
      await fetchProcessingQueue();
    } catch (error) {
      console.error('Error clearing all Celery tasks:', error);
      throw error;
    }
  };

  const fetchQueueRef = useRef(fetchProcessingQueue);
  fetchQueueRef.current = fetchProcessingQueue;

  useEffect(() => {
    if (isLoggedIn) {
      fetchQueueRef.current();
      pollingIntervalRef.current = setInterval(() => fetchQueueRef.current(), 3000);
    } else {
      setProcessingQueue([]);
      setCurrentProcessing(null);
      setQueueUnavailable(false);
      setLastQueueRefresh(null);
    }

    return () => {
      requestSequenceRef.current += 1;
      requestPendingRef.current = false;
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    };
  }, [isLoggedIn]);

  return {
    processingQueue,
    currentProcessing,
    processingPanelOpen,
    setProcessingPanelOpen: handleSetProcessingPanelOpen,
    fetchProcessingQueue,
    clearAllCeleryTasks,
    queueUnavailable,
    lastQueueRefresh,
  };
};

export default useProcessingMonitor;
